/**
 * Permission Context - RBAC Permission Management
 * Manages user privileges extracted from JWT token and provides permission checking
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAppContext } from './AppContainer';

interface PermissionContextValue {
  privileges: string[];
  hasPrivilege: (privilegeName: string) => boolean;
  hasAnyPrivilege: (privilegeNames: string[]) => boolean;
  hasAllPrivileges: (privilegeNames: string[]) => boolean;
  refreshPrivileges: () => void;
  loading: boolean;
}

const PermissionContext = createContext<PermissionContextValue>({
  privileges: [],
  hasPrivilege: () => false,
  hasAnyPrivilege: () => false,
  hasAllPrivileges: () => false,
  refreshPrivileges: () => {},
  loading: true,
});

interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const { userData } = useAppContext();
  const [privileges, setPrivileges] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const decodeJWT = (token: string): any => {
    try {
      const payload = token.split('.')[1];
      if (!payload) {
        return null;
      }
      const decoded = JSON.parse(atob(payload));
      return decoded;
    } catch (error) {
      console.error('Failed to decode JWT token:', error);
      return null;
    }
  };

  useEffect(() => {
    const extractPrivileges = () => {
      if (!userData) {
        setPrivileges([]);
        setLoading(false);
        return;
      }

      try {
        const decoded = decodeJWT(userData);
        if (decoded && decoded.privileges && Array.isArray(decoded.privileges)) {
          setPrivileges(decoded.privileges);
        } else {
          setPrivileges([]);
        }
      } catch (error) {
        console.error('Error extracting privileges from token:', error);
        setPrivileges([]);
      }
      setLoading(false);
    };

    extractPrivileges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const hasPrivilege = (privilegeName: string): boolean => {
    return privileges.includes(privilegeName);
  };

  const hasAnyPrivilege = (privilegeNames: string[]): boolean => {
    return privilegeNames.some(name => privileges.includes(name));
  };

  const hasAllPrivileges = (privilegeNames: string[]): boolean => {
    return privilegeNames.every(name => privileges.includes(name));
  };

  const refreshPrivileges = () => {
    if (!userData) {
      setPrivileges([]);
      return;
    }

    try {
      const decoded = decodeJWT(userData);
      if (decoded && decoded.privileges && Array.isArray(decoded.privileges)) {
        setPrivileges(decoded.privileges);
      } else {
        setPrivileges([]);
      }
    } catch (error) {
      console.error('Error refreshing privileges:', error);
      setPrivileges([]);
    }
  };

  return (
    <PermissionContext.Provider
      value={{
        privileges,
        hasPrivilege,
        hasAnyPrivilege,
        hasAllPrivileges,
        refreshPrivileges,
        loading,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = (): PermissionContextValue => useContext(PermissionContext);
